import { forwardRef } from "react";
import { cn } from "@/lib/utils";
import type { JobStage, PermitStatus, DispatchStatus, IssueSeverity, IssueStatus, MaterialsStatus, MaterialReadiness, CloseoutStatus, OrderStatus, AssignmentType, ContractorResponseStatus, ProcurementStatus, WarehouseStatus, InventoryItemStatus, CreditStatus } from "@/data/types";

const stageColors: Record<JobStage, string> = {
  "Sale Closed": "bg-muted text-muted-foreground",
  "Review and Validation": "bg-info/15 text-info border-info/30",
  "Review Complete": "bg-info/15 text-info border-info/30",
  "Ready for Ordering": "bg-warning/15 text-warning border-warning/30",
  "Ordered": "bg-primary/15 text-primary border-primary/30",
  "Permit and Order Pending": "bg-warning/15 text-warning border-warning/30",
  "Ready for Scheduling": "bg-info/15 text-info border-info/30",
  "Scheduled Tentative": "bg-info/15 text-info border-info/30",
  "Scheduled Confirmed": "bg-primary/15 text-primary border-primary/30",
  "Ready for Dispatch": "bg-accent/15 text-accent border-accent/30",
  "Available for Contractor Assignment": "bg-accent/15 text-accent border-accent/30",
  "Assigned and Dispatched": "bg-primary/15 text-primary border-primary/30",
  "In Progress": "bg-primary/20 text-primary border-primary/40",
  "QA Review": "bg-warning/15 text-warning border-warning/30",
  "Completed": "bg-success/15 text-success border-success/30",
  "Issue": "bg-destructive/15 text-destructive border-destructive/30",
};

const severityColors: Record<IssueSeverity, string> = {
  Low: "bg-muted text-muted-foreground",
  Medium: "bg-warning/15 text-warning border-warning/30",
  Urgent: "bg-destructive/15 text-destructive border-destructive/30",
};

const permitColors: Record<PermitStatus, string> = {
  "Not Required": "bg-muted text-muted-foreground",
  "Submitted": "bg-info/15 text-info border-info/30",
  "Approved": "bg-success/15 text-success border-success/30",
  "Revision Needed": "bg-destructive/15 text-destructive border-destructive/30",
};

const dispatchColors: Record<DispatchStatus, string> = {
  "Not Ready": "bg-muted text-muted-foreground",
  "Ready for Dispatch": "bg-info/15 text-info border-info/30",
  "Sent to Contractor Network": "bg-accent/15 text-accent border-accent/30",
  "Awaiting Response": "bg-warning/15 text-warning border-warning/30",
  "Accepted": "bg-success/15 text-success border-success/30",
  "Rejected": "bg-destructive/15 text-destructive border-destructive/30",
  "Assigned": "bg-primary/15 text-primary border-primary/30",
  "Closed": "bg-muted text-muted-foreground",
};

const issueStatusColors: Record<IssueStatus, string> = {
  Open: "bg-destructive/15 text-destructive border-destructive/30",
  "In Progress": "bg-warning/15 text-warning border-warning/30",
  Resolved: "bg-success/15 text-success border-success/30",
  Closed: "bg-muted text-muted-foreground",
};

type StatusBadgeProps = {
  type: "stage" | "severity" | "permit" | "dispatch" | "issueStatus" | "materials" | "materialReadiness" | "closeout" | "order" | "assignment" | "contractorResponse" | "procurement" | "warehouse" | "inventoryItem" | "creditStatus" | "installStatus" | "qaStatus" | "schedulingStatus";
  value: string;
  className?: string;
};

export const StatusBadge = forwardRef<HTMLSpanElement, StatusBadgeProps>(function StatusBadge({ type, value, className }, ref) {
  let colorClass = "bg-muted text-muted-foreground";

  if (type === "stage") colorClass = stageColors[value as JobStage] || colorClass;
  else if (type === "severity") colorClass = severityColors[value as IssueSeverity] || colorClass;
  else if (type === "permit") colorClass = permitColors[value as PermitStatus] || colorClass;
  else if (type === "dispatch") colorClass = dispatchColors[value as DispatchStatus] || colorClass;
  else if (type === "issueStatus") colorClass = issueStatusColors[value as IssueStatus] || colorClass;
  else if (type === "materials") {
    const m: Record<MaterialsStatus, string> = {
      "Not Ready": "bg-muted text-muted-foreground",
      Ready: "bg-success/15 text-success border-success/30",
      "Missing Items": "bg-destructive/15 text-destructive border-destructive/30",
    };
    colorClass = m[value as MaterialsStatus] || colorClass;
  } else if (type === "materialReadiness") {
    const mr: Record<MaterialReadiness, string> = {
      "Not Ordered": "bg-muted text-muted-foreground",
      "Ordered": "bg-info/15 text-info border-info/30",
      "Partially Received": "bg-warning/15 text-warning border-warning/30",
      "Fully Received": "bg-primary/15 text-primary border-primary/30",
      "Staged for Job": "bg-success/15 text-success border-success/30",
      "Loaded for Crew": "bg-success/20 text-success border-success/40",
      "Missing Items": "bg-destructive/15 text-destructive border-destructive/30",
      "Field Purchase Required": "bg-warning/20 text-warning border-warning/40",
    };
    colorClass = mr[value as MaterialReadiness] || colorClass;
  } else if (type === "closeout") {
    const cl: Record<CloseoutStatus, string> = {
      "Not Started": "bg-muted text-muted-foreground",
      "Install Complete": "bg-info/15 text-info border-info/30",
      "QA Passed": "bg-primary/15 text-primary border-primary/30",
      "Final Photos Complete": "bg-primary/15 text-primary border-primary/30",
      "Issues Resolved": "bg-primary/15 text-primary border-primary/30",
      "Ready for Closeout": "bg-warning/15 text-warning border-warning/30",
      "Closed": "bg-success/15 text-success border-success/30",
    };
    colorClass = cl[value as CloseoutStatus] || colorClass;
  } else if (type === "order") {
    const o: Record<OrderStatus, string> = {
      "Not Ordered": "bg-muted text-muted-foreground",
      Ordered: "bg-info/15 text-info border-info/30",
      Confirmed: "bg-success/15 text-success border-success/30",
      "ETA Received": "bg-warning/15 text-warning border-warning/30",
    };
    colorClass = o[value as OrderStatus] || colorClass;
  } else if (type === "assignment") {
    const a: Record<AssignmentType, string> = {
      "Internal Crew": "bg-primary/15 text-primary border-primary/30",
      Subcontractor: "bg-accent/15 text-accent border-accent/30",
      Unassigned: "bg-muted text-muted-foreground",
    };
    colorClass = a[value as AssignmentType] || colorClass;
  } else if (type === "contractorResponse") {
    const c: Record<ContractorResponseStatus, string> = {
      Accepted: "bg-success/15 text-success border-success/30",
      Rejected: "bg-destructive/15 text-destructive border-destructive/30",
      "Bid Submitted": "bg-info/15 text-info border-info/30",
      "No Response": "bg-muted text-muted-foreground",
      Pending: "bg-warning/15 text-warning border-warning/30",
      Viewed: "bg-primary/10 text-primary border-primary/20",
    };
    colorClass = c[value as ContractorResponseStatus] || colorClass;
  } else if (type === "procurement") {
    const p: Record<ProcurementStatus, string> = {
      "Not Ordered": "bg-muted text-muted-foreground",
      "Ordered": "bg-info/15 text-info border-info/30",
      "Confirmed": "bg-primary/15 text-primary border-primary/30",
      "In Transit": "bg-warning/15 text-warning border-warning/30",
      "Delivered": "bg-success/15 text-success border-success/30",
    };
    colorClass = p[value as ProcurementStatus] || colorClass;
  } else if (type === "warehouse") {
    const w: Record<WarehouseStatus, string> = {
      "Not Received": "bg-muted text-muted-foreground",
      "Partially Received": "bg-warning/15 text-warning border-warning/30",
      "Received": "bg-info/15 text-info border-info/30",
      "Staged": "bg-primary/15 text-primary border-primary/30",
      "Loaded": "bg-success/15 text-success border-success/30",
      "Missing Items": "bg-destructive/15 text-destructive border-destructive/30",
    };
    colorClass = w[value as WarehouseStatus] || colorClass;
  } else if (type === "inventoryItem") {
    const inv: Record<InventoryItemStatus, string> = {
      "In Stock": "bg-success/15 text-success border-success/30",
      "Reserved": "bg-info/15 text-info border-info/30",
      "Allocated": "bg-primary/15 text-primary border-primary/30",
      "Low Stock": "bg-warning/15 text-warning border-warning/30",
    };
    colorClass = inv[value as InventoryItemStatus] || colorClass;
  } else if (type === "creditStatus") {
    const cs: Record<CreditStatus, string> = {
      "Draft": "bg-muted text-muted-foreground",
      "Proposed": "bg-info/15 text-info border-info/30",
      "Approved": "bg-primary/15 text-primary border-primary/30",
      "Offered to Customer": "bg-warning/15 text-warning border-warning/30",
      "Accepted": "bg-success/15 text-success border-success/30",
      "Applied": "bg-success/20 text-success border-success/40",
      "Declined": "bg-destructive/15 text-destructive border-destructive/30",
      "Canceled": "bg-muted text-muted-foreground",
    };
    colorClass = cs[value as CreditStatus] || colorClass;
  } else if (type === "installStatus") {
    const is: Record<string, string> = {
      "Not Started": "bg-muted text-muted-foreground",
      "Scheduled": "bg-info/15 text-info border-info/30",
      "En Route": "bg-primary/15 text-primary border-primary/30",
      "On Site": "bg-primary/20 text-primary border-primary/40",
      "In Progress": "bg-primary/20 text-primary border-primary/40",
      "Install Complete": "bg-success/15 text-success border-success/30",
    };
    colorClass = is[value] || colorClass;
  } else if (type === "qaStatus") {
    const qs: Record<string, string> = {
      "Not Started": "bg-muted text-muted-foreground",
      "In Progress": "bg-warning/15 text-warning border-warning/30",
      "Submitted": "bg-info/15 text-info border-info/30",
      "Passed": "bg-success/15 text-success border-success/30",
      "Failed": "bg-destructive/15 text-destructive border-destructive/30",
    };
    colorClass = qs[value] || colorClass;
  } else if (type === "schedulingStatus") {
    const ss: Record<string, string> = {
      "Not Ready": "bg-muted text-muted-foreground",
      "Unscheduled": "bg-warning/15 text-warning border-warning/30",
      "Scheduled": "bg-info/15 text-info border-info/30",
      "Crew Assigned": "bg-primary/15 text-primary border-primary/30",
      "Confirmed": "bg-success/15 text-success border-success/30",
    };
    colorClass = ss[value] || colorClass;
  }

  return (
    <span ref={ref} className={cn("inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium whitespace-nowrap", colorClass, className)}>
      {value}
    </span>
  );
});
